import { ToolBar } from '@picsart/editorlayout';
import { useMemo, Dispatch, SetStateAction, memo, useCallback, useState, useRef, MouseEvent as ReactMouseEvent } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';
import { BadgeTypes } from '@picsart/badge';

import {
  ALLOWED_LIMIT_FILES,
  BATCH_ERROR,
  BATCH_TOOLS,
  batchToolsList as initialBatchToolsList,
} from 'pages/Batch/configs';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import errorLogger from 'services/errorLogger';
import useMemoSelector from 'hooks/useMemoSelector';
import { getUserData } from 'store/selectors/general';
import analytics from 'pages/Batch/utils/analytics';
import { IBatchTool } from 'types/store';
import ConfirmModal from 'components/common/Modals/ConfirmModal';
import { confirmModalTypes } from 'components/common/Modals/ConfirmModal/types';
import useToolCancelApply from 'hooks/useToolCancelApply';
import useAuthInfo from 'hooks/useAuthInfo';
import useFileUploader from 'hooks/useFileUploader';
import useUploadNotification from 'pages/Batch/utils/useUploadNotification';

const SidebarTools = ({ setSidebarOpen }: { setSidebarOpen: Dispatch<SetStateAction<boolean>> }) => {
  const translate = useTranslator();
  const { allSelected, data, setActiveTool, activeTool, setBottomSheetCustomHeight, selectedData, acceptFiles, isWeb } = useBatchData();
  const { showUploaderError } = useUploadNotification();
  const remainingImagesCount = ALLOWED_LIMIT_FILES - data.length;

  const { inputProps, handleUploadClick } = useFileUploader({
    maxCount: remainingImagesCount,
    onFileUploadDone: acceptFiles,
  });
  const { handleCancel, handleDone, isDone } = useToolCancelApply();
  const { isProOrGoldUser } = useAuthInfo();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [targetToolBarType, setTargetToolBarType] = useState<BATCH_TOOLS | null>(null);
  const toolbarItemRef = useRef();
  const { isProFeatureEnabledForLocation, waitingUserSubscription } = useMemoSelector(getUserData);

  const batchToolsList = useMemo(() => initialBatchToolsList.filter((tool: IBatchTool) => tool.type !== BATCH_TOOLS.enhance || isWeb).map((tool: IBatchTool) => {
    const updatedTool = { ...tool };

    const userSubscriptionResolved = !waitingUserSubscription && isProFeatureEnabledForLocation !== null;
    if (tool.isPremium && userSubscriptionResolved && !isProOrGoldUser) {
      updatedTool.badge = isProFeatureEnabledForLocation ? BadgeTypes.Pro : BadgeTypes.Premium;
    }

    delete updatedTool.isPremium;

    return updatedTool;
  }), [isProFeatureEnabledForLocation, isProOrGoldUser, isWeb, waitingUserSubscription]);

  const onUploadClick = useCallback((ev: ReactMouseEvent<HTMLElement>) => {
    if (remainingImagesCount > 0) {
      handleUploadClick(ev);
    } else {
      showUploaderError(BATCH_ERROR.exceededFilesLimit);
    }
  }, [remainingImagesCount, showUploaderError, handleUploadClick]);

  const handleChange = useCallback((type: BATCH_TOOLS) => {
    setActiveTool(type);
    if (type === BATCH_TOOLS.upload) return;
    const selectedItems = selectedData.map(x => x.key);
    analytics.openTool(type, allSelected, selectedItems);
    errorLogger.leaveBreadcrumb('tool_change', { tool: type });
  }, [allSelected, setActiveTool, selectedData]);

  const toolBarItems = useMemo(() => [...batchToolsList.map(toolBarItem => {
    const newA = {
      ...toolBarItem,
      isDisabled: (!data.length && toolBarItem.type !== BATCH_TOOLS.upload),
      onClick: (ev: any) => {
        const { bottomSheetHeight } = toolBarItem;

        analytics.clickEvent(toolBarItem.type);

        if (toolBarItem.type === BATCH_TOOLS.upload) {
          if (!activeTool) {
            onUploadClick(ev);
          } else {
            toolbarItemRef.current = ev;
            setTargetToolBarType(toolBarItem.type);
            setShowConfirmModal(true);
          }
        } else {
          if (!activeTool) {
            setSidebarOpen(true);
            handleChange(toolBarItem.type);
          }

          if (activeTool && activeTool !== toolBarItem.type) {
            setTargetToolBarType(toolBarItem.type);
            setShowConfirmModal(true);
          }
        }

        if (bottomSheetHeight) {
          setBottomSheetCustomHeight(bottomSheetHeight);
        }
      },
    };

    delete newA.bottomSheetHeight;

    return newA;
  })], [batchToolsList, data.length, activeTool, onUploadClick, setSidebarOpen, handleChange, setBottomSheetCustomHeight]);

  const handleAction = useCallback(async (type: confirmModalTypes) => {
    if (type === confirmModalTypes.firstChoiceAction) {
      handleCancel();
    } else if (type === confirmModalTypes.secondChoiceAction) {
      if (targetToolBarType === BATCH_TOOLS.upload) {
        await handleDone();
        toolbarItemRef.current && onUploadClick(toolbarItemRef.current);

        setShowConfirmModal(false);
        return;
      }

      if (targetToolBarType) {
        await handleDone();
        setSidebarOpen(true);
        handleChange(targetToolBarType);
      }
    }
    setShowConfirmModal(false);
  }, [handleCancel, targetToolBarType, onUploadClick, handleDone, setSidebarOpen, handleChange]);

  return (
    <>
      <ConfirmModal
        showCloseIcon
        show={showConfirmModal}
        onAction={handleAction}
        title={translate(isDone ? 'Are you sure?' : 'Cancel processing?')}
        description={translate(isDone ? 'Tap Apply to save your edits before switching.' : 'We\'re still working on your images. It shouldn\'t take too long.')}
        firstChoice="Cancel"
        secondChoice={isDone ? 'Apply' : 'Wait'}
        showTickIcon
        isDisabledSecondChoice={!isDone}
      />

      <input {...inputProps} />
      <ToolBar items={toolBarItems} />
    </>
  );
};

export default memo(SidebarTools);
